import CarStorageHero from 'assets/images/psp/CarStorage/car-storage-hero.webp';
import CarStorage1 from 'assets/images/psp/CarStorage/car-related1.jpg';
import CarStorage2 from 'assets/images/psp/CarStorage/motorcycle-storage.jpg';
import CarStorage3 from 'assets/images/psp/CarStorage/Winterizing-Your-RV.png';
import CarStorage4 from 'assets/images/psp/CarStorage/car-related4.jpg';
import { storageCategories } from '../utils/getPSPLinks';

const heroContent = {
	title: 'Find Affordable Car Self Storage',
	description:
		"Keep your driveway clear and your car safe with reliable car storage options that fit your needs and budget. Whether you need short-term storage for some time away, seasonal protection for your sports car, or long-term storage while you're deployed overseas, SelfStorage will help you find a secure car storage unit with the right terms and competitive rates.\n\nBrowse indoor and outdoor vehicle storage options that can accommodate everything from a compact sedan to your tried and true work truck to find peace of mind and a price that fits your budget.",
	image: CarStorageHero,
	storageType: 'Car Storage',
	CTAtext: 'Find Car Self Storage',
};

const sizeChartContent = [
	{
		type: 'cardStrip',
		props: {
			heading: 'Get to Know Your Cheapest Car Storage Options',
			description:
				"Vehicle storage comes in a wide range of unit sizes that can accommodate different vehicle makes and models.\n\nTo [find the right fit for your car or truck](https://www.selfstorage.com/blog/car-storage-size-guide/), it's important to measure the height, length, and width of your car and compare it to the dimensions of your car storage unit.\n\nThis will ensure your vehicle fits, and may even provide you with a little extra room to store tools and other gear your home doesn't have room for.",
			cardData: [
				{
					title: 'Indoor Car Storage',
					description:
						'Fully enclosed storage within a climate controlled building with roll-up doors',
					content:
						'**Available Sizes:** 10x15, 10x20, 10x25\n\n**Average Monthly Cost:** $100-450\n\n**Best For:** Classic cars, luxury vehicles, long-term storage, and extreme weather climates',
					contentAlign: 'text-left',
				},
				{
					title: 'Covered Car Storage',
					description:
						'Partial shelter with roof protection but open sides, carport-style structure',
					content:
						'**Available Sizes:** 10x20, 10x25\n\n**Average Monthly Cost:** $50-150\n\n**Best For:** Daily drivers, moderate weather protection, cost-conscious storage',
					contentAlign: 'text-left',
				},
				{
					title: 'Outdoor Car Storage',
					description:
						'Open-air parking space on paved surface with perimeter fencing and security',
					content:
						'**Available Sizes:** 10x15, 10x20, 10x25\n\n**Average Monthly Cost:** $30-100\n\n**Best For:** Short-term storage, travel, deployment, oversized vehicles, and budget-savvy families',
					contentAlign: 'text-left',
				},
			],
		},
	},
];

const videoContent = {
	title: 'Car Storage Video section',
	background: 'blueGradient',
	modules: [
		{
			type: 'video',
			props: {
				content: {
					title: 'How to Prepare Your Car for Storage',
					description:
						'Before you can put your car in a storage unit or covered storage space, you’ll need to complete a few administrative and maintenance tasks to prepare your vehicle.',
					videoSrc: 'https://youtube.com/embed/kr8OHVDWjyA',
				},
			},
		},
	],
};

const reviewsContent = {
	headerTitle: 'Car Self Storage: ',
	reviews: [
		{
			title: 'Easy-to-use website',
			stars: 5,
			text: 'Easy to easy-to-use website for booking my new storage unit online. Good prices for car storage.',
			customerName: 'CJ',
			date: 'March 10, 2024',
			link: 'https://www.trustpilot.com/reviews/66328dbdde5566d2d319d122',
		},
		{
			title: 'Awesomeness',
			stars: 5,
			text: 'I was able to do the entire reservation process while in the passenger seat of a car driving to the storage lot!',
			customerName: 'Robert Ober',
			date: 'April 18, 2024',
			link: 'https://www.trustpilot.com/reviews/63dbe1374b01fb3f5494d79b',
		},
		{
			title: 'The whole experience went quickly and...',
			stars: 5,
			text: 'The whole experience went quickly and smoothly. The SelfStorage agent was pleasant and efficient. The price was right. The outdoor car lot was well-lit and very secure. I am pleased with my decision to store our vehicle here.',
			customerName: 'Lisa Dahn',
			date: 'June 5, 2024',
			link: 'https://www.trustpilot.com/reviews/61696a7e75069a4da48610c2',
		},
		{
			title: 'Top notch service',
			stars: 5,
			text: 'I was able to reserve and pay online for the parking space to store my car without having to go to their office. Their follow-up phone call and emails made it a very simple and easy-to-follow process.',
			customerName: 'Bill',
			date: 'August 12, 2024',
			link: 'https://www.trustpilot.com/reviews/5fb408465e693f0a8429d399',
		},
	],
};

const faqSectionContent = {
	header: 'Car Self Storage FAQs',
	faqs: [
		{
			question: 'Is it legal to store a car in a storage unit?',
			answer: "Yes, you can legally store a car in a storage unit, but you need to choose the right type of facility. Standard indoor storage units typically aren't designed for vehicles due to ventilation, door size, and safety requirements.\n\n However, many self storage facilities offer specialized vehicle storage units with roll-up doors, proper ventilation, and vehicle-friendly dimensions.\n\n Always check with your chosen facility about their specific vehicle storage policies and any restrictions on fuel levels or battery disconnection requirements. **SelfStorage.com** lets you search thousands of car storage units and reserve online, all in one easy-to-use website.",
		},
		{
			question: 'Should you change your oil before storing a car?',
			answer: "Yes, you should change your oil before long-term storage. Used oil contains contaminants and can damage your engine components during extended storage periods. \n\nFresh oil provides better protection for internal engine parts and helps prevent corrosion. This is especially important if you're storing your vehicle for more than a few months.",
		},
		{
			question: 'Where can I store my car for 6 months?',
			answer: '**SelfStorage.com** shows you vehicle storage options perfect for 6-month storage needs:\n\n- Outdoor vehicle storage is the most budget-friendly option with easy access as a primary benefit.\n- Covered storage offers more weather protection while maintaining affordability.\n- Indoor climate controlled units provide premium protection for valuable or classic vehicles at a higher cost.\n\n**SelfStorage.com** helps you compare facilities in your area, filter for your needs, and find the best rates for your specific storage timeline. Many facilities offer discounts for longer-term rentals, like 6-month commitments.',
		},
		{
			question: 'Can you fit a car in a 10x10 storage unit?',
			answer: "A 10x10 storage unit is too small for most cars. Most vehicles need at least a 10x15 unit to fit comfortably, with 10x20 being the most common car storage unit size. Here's what typically fits in different sizes:\n\n- 10x15 - Compact cars like the Honda Civic and Toyota Corolla.\n- 10x20 - Mid-size sedans and small SUVs like the Honda Accord, Toyota Camry, or Honda CR-V.\n- 10x25 - Full-size vehicles and trucks, comfortably storing models like the Ford F-150 or Chevrolet Silverado.",
		},
	],
};

const relatedContent = {
	heading: 'Learn All About Car Self Storage',
	type: 'unitCards',
	id: 'car-storage-related-content',
	data: {
		slug: 'car-storage-related-content',
		unitCards: [
			{
				title: 'Storage Tips',
				url: 'https://www.selfstorage.com/blog/car-storage-size-guide/',
				layout: false,
				plain: true,
				date: 'March 3, 2025',
				image: CarStorage1,
				content: 'Choosing the Right Storage Unit for Your Vehicle',
			},
			{
				title: 'Storage Tips',
				url: 'https://www.selfstorage.com/blog/guide-to-motorcycle-winter-storage/',
				layout: false,
				plain: true,
				date: 'October 5, 2023',
				image: CarStorage2,
				content: 'Quick Guide to Proper Motorcycle Winter Storage',
			},
			{
				title: 'Storage Tips',
				url: 'https://www.selfstorage.com/blog/rv-winterization-guide/',
				layout: false,
				plain: true,
				date: 'January 21, 2025',
				image: CarStorage3,
				content: 'RV & Camper Winterization',
			},
			{
				title: 'Storage Tips',
				url: 'https://www.selfstorage.com/blog/long-term-rv-storage-tips-2/',
				layout: false,
				plain: true,
				date: 'September 9, 2025',
				image: CarStorage4,
				content:
					'RV Storage: Long-Term Storage Types & Tips to Prepare',
			},
		],
	},
};

const moreWaysToStoreContent = [
	{
		type: 'links',
		props: {
			heading: {
				headingName: 'More ways to store',
			},
			type: 'unitCards',
			id: 'storage size unit cards',
			linksList: storageCategories.car,
		},
	},
];

export const CarStorageContent = {
	type: 'Car',
	reviews: reviewsContent.reviews,
	sections: [
		{
			title: 'Car Storage Hero',
			background: 'default',
			modules: [
				{
					type: 'hero',
					id: 'car-storage-hero',
					props: {
						content: heroContent,
						additionalProps: {
							heading: {
								size: 'large',
								color: 'white',
								weight: 'ultraBold',
							},
						},
					},
				},
			],
		},
		{
			title: 'Car Storage Size Chart',
			modules: sizeChartContent,
		},
		videoContent,
		{
			title: 'Reviews Section',
			background: 'gray',
			modules: [
				{
					type: 'reviews',
					title: 'Reviews Section',
					slug: 'car-storage-reviews',
					props: {
						reviewsData: reviewsContent,
					},
				},
			],
		},
		{
			title: 'Faqs Section',
			modules: [
				{
					type: 'faqs',
					props: {
						content: faqSectionContent,
					},
				},
			],
		},
		{
			title: 'Related Content',
			background: 'darkBlue',
			modules: [
				{
					type: 'featuredContent',
					props: {
						data: relatedContent,
					},
				},
			],
		},
		{
			title: 'Link to other PSPs',
			background: 'lighterBlue',
			modules: moreWaysToStoreContent,
		},
		{
			background: 'gray',
			modules: [
				{
					type: 'storageNearYou',
					props: {
						type: 'Car',
					},
				},
			],
		},
	],
};
